import { useState } from 'react';
import { useToastController } from 'smoothie';

import { StorageKeys } from '../../../common/constants';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useStorage } from '../../../common/providers/StorageProvider';
import type { User } from '../../../user/types/User';
import { type CustomerEdd, useCreateEdd } from '../../hooks/useCreateEdd';
import { UpdateEmployment, UpdateEmploymentModal } from './UpdateEmployment';

const shouldShowEmploymentPrompt = (
    user: User,
    lastSeen: string | null | undefined,
) => {
    if (lastSeen == null) {
        return user.needsEmploymentUpdate === true;
    }
    const notSeenInLastWeek =
        Date.now() - new Date(lastSeen).getTime() > 7 * 24 * 60 * 60 * 1000;
    return notSeenInLastWeek && user.needsEmploymentUpdate === true;
};

export const EmploymentPrompt = () => {
    const user = useAuthedUser();
    const storage = useStorage();
    const toast = useToastController();

    const employmentPromptTimestamp = storage.getString(
        StorageKeys.EMPLOYMENT_UPDATE_PROMPT_TIMESTAMP,
    );

    const showEmploymentPrompt = shouldShowEmploymentPrompt(
        user,
        employmentPromptTimestamp,
    );

    const [showNeedsUpdateModal, setShowNeedsUpdateModal] =
        useState(showEmploymentPrompt);
    const [showEmploymentSheet, setShowEmploymentSheet] = useState(false);

    const { mutate: createEdd } = useCreateEdd();

    const storeEmploymentPromptLastSeen = () => {
        storage.setString(
            StorageKeys.EMPLOYMENT_UPDATE_PROMPT_TIMESTAMP,
            new Date().toString(),
        );
    };

    const canBeDismissed = new Date() < new Date('2025-03-01');

    return (
        <>
            <UpdateEmployment
                isOpen={showNeedsUpdateModal}
                canBeDismissed={canBeDismissed}
                onClose={() => {
                    setShowNeedsUpdateModal(false);
                    storeEmploymentPromptLastSeen();
                }}
                onContinue={() => {
                    setShowNeedsUpdateModal(false);
                    setShowEmploymentSheet(true);
                }}
            />
            <UpdateEmploymentModal
                isOpen={showEmploymentSheet}
                canBeDismissed={canBeDismissed}
                onClose={() => {
                    setShowEmploymentSheet(false);
                    storeEmploymentPromptLastSeen();
                }}
                onContinue={(data: CustomerEdd) => {
                    storeEmploymentPromptLastSeen();
                    setShowEmploymentSheet(false);
                    createEdd(
                        {
                            body: data,
                            urlParams: {
                                userId: user.id,
                            },
                        },
                        {
                            onSuccess: () => {
                                toast.show(
                                    'Thanks for updating your employment status!',
                                );
                            },
                            onError: () => {
                                toast.show(
                                    'An error occurred while updating your employment status.',
                                );
                            },
                        },
                    );
                }}
            />
        </>
    );
};
